<template>
  <div class="personal-info-container">
    <!-- 标题 -->
    <h1 class="title">Hi, Let me know about you</h1>
    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- 表单区域 -->
      <div class="form-content">
        <!-- 邮箱地址 -->
        <div class="form-row">
          <label class="form-label">Your email address</label>
          <input
            v-model="email"
            type="email"
            placeholder="qahdjcjkbgk@gmail.com"
            class="form-input"
            @input="validateEmail"
          />
        </div>
        
        <!-- 称呼 -->
        <div class="form-row">
          <label class="form-label">How to address you</label>
          <input
            v-model="name"
            type="text"
            placeholder="Water"
            class="form-input"
            @input="validateName"
          />
        </div>
        
        <!-- 身材类型选择 -->
        <div class="form-row">
          <label class="form-label">身材类型</label>
          <div class="body-type-picker">
            <div class="body-type-list" ref="bodyTypeList" @scroll="handleBodyTypeScroll">
              <div 
                v-for="(type, index) in bodyTypes" 
                :key="index"
                :class="['body-type-item', { 'selected': selectedBodyTypeIndex === index }]"
                @click="selectBodyType(index)"
              >
                <img :src="type.image" :alt="type.name" class="body-type-image" />
                <span class="body-type-name">{{ type.name }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- 身高选择 -->
        <div class="form-row">
          <label class="form-label">身高 (cm)</label>
          <div class="picker-container">
            <div class="picker-overlay">
              <div class="picker-center"></div>
            </div>
            <div class="picker-scroll" ref="heightPicker" @scroll="handleHeightScroll">
              <div class="picker-padding"></div>
              <div 
                v-for="(h, index) in heightOptions" 
                :key="index"
                :class="['picker-item', { 'selected': selectedHeightIndex === index }]"
                @click="selectHeight(index)"
              >
                {{ h }}
              </div>
              <div class="picker-padding"></div>
            </div>
          </div>
        </div>
        
        <!-- 体重选择 -->
        <div class="form-row">
          <label class="form-label">体重 (kg)</label>
          <div class="picker-container">
            <div class="picker-overlay">
              <div class="picker-center"></div>
            </div>
            <div class="picker-scroll" ref="weightPicker" @scroll="handleWeightScroll">
              <div class="picker-padding"></div>
              <div 
                v-for="(w, index) in weightOptions" 
                :key="index"
                :class="['picker-item', { 'selected': selectedWeightIndex === index }]"
                @click="selectWeight(index)"
              >
                {{ w }}
              </div>
              <div class="picker-padding"></div>
            </div>
          </div>
        </div>
      </div>
      
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import hourglassImg from '@/assets/images/character/hourglass.svg'
import triangleImg from '@/assets/images/character/triangle.svg'
import invertedTriangleImg from '@/assets/images/character/invertedTriangle.svg'
import ovalImg from '@/assets/images/character/oval.png'
import rectangleImg from '@/assets/images/character/rectangle.svg'

// 定义 emits
const emit = defineEmits(['next', 'validation-change'])

// 定义 props 来接收回填数据
const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({})
  }
})

// 响应式数据（添加默认值或回填数据）
const email = ref(props.initialData.email || 'test@gmail.com')
const name = ref(props.initialData.name || 'John')
const height = ref(props.initialData.height || '170')
const weight = ref(props.initialData.weight || '60')
const isLoading = ref(false)

// 身材类型选项
const bodyTypes = ref([
  { name: '沙漏型', image: hourglassImg },
  { name: '梨形', image: triangleImg },
  { name: 'T字型', image: invertedTriangleImg },
  { name: 'O字型', image: ovalImg },
  { name: 'H型', image: rectangleImg }
])
const selectedBodyTypeIndex = ref(2) // 默认选中中间的

// 生成身高选项 (140-190, 步长5)
const heightOptions = ref([])
for (let i = 140; i <= 190; i += 5) {
  heightOptions.value.push(`${i} - ${i + 5}`)
}

// 生成体重选项 (40-85, 步长5)
const weightOptions = ref([])
for (let i = 40; i <= 85; i += 5) {
  weightOptions.value.push(`${i} - ${i + 5}`)
}

// 选中的索引
const selectedHeightIndex = ref(Math.floor(heightOptions.value.length / 2)) // 默认中间
const selectedWeightIndex = ref(Math.floor(weightOptions.value.length / 2)) // 默认中间

// Refs for picker elements
const heightPicker = ref(null)
const weightPicker = ref(null)
const bodyTypeList = ref(null)

// 验证邮箱格式（需要在计算属性之前定义）
const validateEmailFormat = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

// 计算属性
const isFormValid = computed(() => {
  return email.value && name.value && height.value && weight.value && 
         validateEmailFormat(email.value) && 
         parseFloat(height.value) > 0 && parseFloat(weight.value) > 0
})

// 监听验证状态变化并通知父组件
watch(isFormValid, (newValue) => {
  nextTick(() => {
    emit('validation-change', newValue)
  })
})

// 监听props变化，更新本地数据
watch(() => props.initialData, (newInitialData) => {
  if (newInitialData) {
    if (newInitialData.email !== undefined) email.value = newInitialData.email
    if (newInitialData.name !== undefined) name.value = newInitialData.name
    if (newInitialData.height !== undefined) height.value = newInitialData.height.toString()
    if (newInitialData.weight !== undefined) weight.value = newInitialData.weight.toString()
  }
}, { deep: true })

// 组件挂载后初始化选择器位置
onMounted(() => {
  nextTick(() => {
    // 初始化身高选择器位置
    if (heightPicker.value) {
      scrollToCenter(heightPicker.value, selectedHeightIndex.value)
    }
    // 初始化体重选择器位置
    if (weightPicker.value) {
      scrollToCenter(weightPicker.value, selectedWeightIndex.value)
    }
    // 初始化身材类型选择器位置
    if (bodyTypeList.value) {
      selectBodyType(selectedBodyTypeIndex.value)
    }
    // 设置初始值
    height.value = heightOptions.value[selectedHeightIndex.value]
    weight.value = weightOptions.value[selectedWeightIndex.value]
    
    emit('validation-change', isFormValid.value)
  })
})

// 验证邮箱输入
const validateEmail = () => {
  // 可以在这里添加实时验证逻辑
}

// 验证姓名输入
const validateName = () => {
  // 可以在这里添加实时验证逻辑
}

// 身材类型选择
const selectBodyType = (index) => {
  selectedBodyTypeIndex.value = index
  // 滚动到选中项
  if (bodyTypeList.value) {
    const items = bodyTypeList.value.querySelectorAll('.body-type-item')
    if (items[index]) {
      items[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
    }
  }
}

const handleBodyTypeScroll = () => {
  // 可以添加滚动时自动对齐的逻辑
}

// 身高选择
const selectHeight = (index) => {
  selectedHeightIndex.value = index
  height.value = heightOptions.value[index]
  scrollToCenter(heightPicker.value, index)
}

const handleHeightScroll = () => {
  if (!heightPicker.value) return
  const scrollTop = heightPicker.value.scrollTop
  const itemHeight = 44 // 每个选项的高度
  const centerIndex = Math.round(scrollTop / itemHeight)
  if (centerIndex !== selectedHeightIndex.value && centerIndex >= 0 && centerIndex < heightOptions.value.length) {
    selectedHeightIndex.value = centerIndex
    height.value = heightOptions.value[centerIndex]
  }
}

// 体重选择
const selectWeight = (index) => {
  selectedWeightIndex.value = index
  weight.value = weightOptions.value[index]
  scrollToCenter(weightPicker.value, index)
}

const handleWeightScroll = () => {
  if (!weightPicker.value) return
  const scrollTop = weightPicker.value.scrollTop
  const itemHeight = 44 // 每个选项的高度
  const centerIndex = Math.round(scrollTop / itemHeight)
  if (centerIndex !== selectedWeightIndex.value && centerIndex >= 0 && centerIndex < weightOptions.value.length) {
    selectedWeightIndex.value = centerIndex
    weight.value = weightOptions.value[centerIndex]
  }
}

// 滚动到居中位置
const scrollToCenter = (element, index) => {
  if (!element) return
  const itemHeight = 44
  element.scrollTo({
    top: index * itemHeight,
    behavior: 'smooth'
  })
}

// 处理下一步
const handleSubmit = () => {
  if (!isFormValid.value) return
  
  const stepData = {
    email: email.value,
    name: name.value,
    height: height.value,
    weight: weight.value,
    bodyType: bodyTypes.value[selectedBodyTypeIndex.value].name
  }
  
  emit('next', stepData)
}

// 暴露方法给父组件调用
defineExpose({
  handleSubmit
})
</script>

<style scoped>
/* 主容器 */
.personal-info-container {
  width: 100%;
  max-width: 700px;
  margin: 0 auto;
  min-height: 450px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;  /* 为箭头提供定位参考 */
}

/* 移动端基础优化 */
@media (max-width: 768px) {
  .personal-info-container {
    max-width: 100%;
    min-height: 100vh;
    padding: 20px 16px;
    justify-content: center; /* 垂直居中 */
    align-items: center; /* 水平居中 */
  }
}

/* 标题样式 */
.title {
  color: rgba(255, 255, 255, 0.95);
  font-size: 32px;
  font-weight: 400;
  margin-bottom: 40px;
  letter-spacing: 0.8px;
  line-height: 1.2;
  text-align: center;
}

/* 主要内容区域 - 表单和右箭头 */
.main-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 40px;
  position: relative;
  width: 100%;
  flex: 1;
  min-height: 350px;
  transform: translateX(-70px);  /* 向左移动10px */
}

/* 移动端主要内容区域优化 */
@media (max-width: 768px) {
  .main-content {
    min-height: auto;
    gap: 24px;
    padding: 0;
    transform: none;
    flex-direction: column;
    align-items: stretch;
    justify-content: center;
    width: 100%;
    max-width: 400px;
  }
}

/* 表单内容区域 */
.form-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 40px;
  max-width: 600px;
  margin: 0 auto;
  margin-right: 80px;  /* 距离右边80px，您可以调整这个值 */
}

/* 移动端表单内容区域优化 */
@media (max-width: 768px) {
  .form-content {
    gap: 24px;
    max-width: 100%;
    margin-right: 0;
    width: 100%;
    flex: 1;
    justify-content: center;
  }
}

/* 表单行 - 标签和输入框在同一行 */
.form-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 0 20px;
}

/* 移动端表单行优化 */
@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    padding: 0;
  }
}

/* 标签样式 */
.form-label {
  color: rgba(255, 255, 255, 0.9);
  font-size: 16px;
  font-weight: 400;
  letter-spacing: 0.3px;
  width: 200px;
  text-align: right;
  flex-shrink: 0;
}

/* 移动端标签样式优化 */
@media (max-width: 768px) {
  .form-label {
    width: auto;
    text-align: left;
    font-size: 14px;
    margin-bottom: 4px;
  }
}

/* 输入框样式 */
.form-input {
  width: 280px;
  padding: 12px 16px;
  background: rgba(45, 55, 72, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.95);
  font-size: 16px;
  transition: all 0.3s ease;
  outline: none;
  backdrop-filter: blur(10px);
}

/* 移动端输入框样式优化 */
@media (max-width: 768px) {
  .form-input {
    width: 100%;
    padding: 12px 16px;
    font-size: 16px; /* 防止iOS缩放 */
    border-radius: 8px;
    min-height: 48px; /* 触摸友好 */
  }
}

.form-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
  font-size: 16px;
}

.form-input:focus {
  background: rgba(45, 55, 72, 0.9);
  border-color: rgba(255, 255, 255, 0.4);
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.1);
}

.form-input:hover:not(:focus) {
  background: rgba(45, 55, 72, 0.85);
  border-color: rgba(255, 255, 255, 0.3);
}

/* 带单位的输入框容器 */
.input-with-unit {
  position: relative;
  width: 280px;
}

/* 移动端带单位输入框容器优化 */
@media (max-width: 768px) {
  .input-with-unit {
    width: 100%;
  }
}

/* 带单位的输入框 */
.with-unit-input {
  padding-right: 50px !important; /* 为内部单位标签留出空间 */
}

/* 内部单位标签样式 */
.unit-inside {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  font-weight: 400;
  pointer-events: none;
  z-index: 1;
}

/* 数字输入框特殊样式 */
.form-input[type="number"] {
  font-family: 'Segoe UI', 'Arial', monospace;
  font-variant-numeric: tabular-nums;
}

/* 隐藏数字输入框的箭头 */
.form-input[type="number"]::-webkit-outer-spin-button,
.form-input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.form-input[type="number"] {
  -moz-appearance: textfield;
}



/* 移动端标题优化 */
@media (max-width: 768px) {
  .title {
    font-size: 24px;
    margin-bottom: 24px;
    line-height: 1.3;
  }
  
  .unit-inside {
    font-size: 14px;
    right: 16px;
  }

  /* 触摸优化 */
  .form-input:focus {
    transform: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.2);
  }
}

@media (max-width: 480px) {
  .personal-info-container {
    min-height: 100vh;
    padding: 16px 12px;
    justify-content: center; /* 保持垂直居中 */
  }
  
  .main-content {
    min-height: auto;
    gap: 20px;
    padding: 0;
    max-width: 350px;
  }
  
  .title {
    font-size: 20px;
    margin-bottom: 20px;
    line-height: 1.2;
  }
  
  .form-content {
    gap: 20px;
    max-width: 100%;
  }
  
  .form-row {
    padding: 0;
  }
  
  .form-label {
    font-size: 13px;
    margin-bottom: 6px;
  }
  
  .form-input {
    padding: 12px 14px;
    font-size: 16px; /* 防止iOS缩放 */
    min-height: 44px;
  }
  
  .unit-inside {
    font-size: 13px;
    right: 14px;
  }
  
  .with-unit-input {
    padding-right: 40px !important;
  }
}

/* 超小屏幕优化 (320px以下) */
@media (max-width: 320px) {
  .personal-info-container {
    padding: 12px 8px;
    justify-content: center; /* 保持垂直居中 */
  }
  
  .main-content {
    max-width: 300px;
  }
  
  .title {
    font-size: 18px;
    margin-bottom: 16px;
  }
  
  .form-content {
    gap: 18px;
  }
  
  .form-input {
    padding: 10px 12px;
    font-size: 16px;
    min-height: 44px;
  }
  
  .unit-inside {
    font-size: 12px;
    right: 12px;
  }
  
  .with-unit-input {
    padding-right: 35px !important;
  }
}

/* 身材类型选择器样式 */
.body-type-picker {
  width: 400px;
  overflow: hidden;
}

.body-type-list {
  display: flex;
  gap: 20px;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 10px 100px;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.body-type-list::-webkit-scrollbar {
  display: none;
}

.body-type-item {
  flex-shrink: 0;
  width: 100px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  opacity: 0.4;
  transform: scale(0.85);
}

.body-type-item.selected {
  opacity: 1;
  transform: scale(1);
}

.body-type-image {
  width: 80px;
  height: 120px;
  object-fit: contain;
  filter: brightness(0.6);
  transition: filter 0.3s ease;
}

.body-type-item.selected .body-type-image {
  filter: brightness(1);
}

.body-type-name {
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  text-align: center;
  transition: color 0.3s ease;
}

.body-type-item.selected .body-type-name {
  color: rgba(255, 255, 255, 1);
  font-weight: 500;
}

/* 选择器容器样式 */
.picker-container {
  position: relative;
  width: 280px;
  height: 180px;
  overflow: hidden;
}

.picker-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 2;
}

.picker-overlay::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 68px;
  background: linear-gradient(to bottom, rgba(45, 55, 72, 0.95), rgba(45, 55, 72, 0));
}

.picker-overlay::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 68px;
  background: linear-gradient(to top, rgba(45, 55, 72, 0.95), rgba(45, 55, 72, 0));
}

.picker-center {
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 44px;
  transform: translateY(-50%);
  background: rgba(166, 245, 93, 0.1);
  border-top: 1px solid rgba(166, 245, 93, 0.5);
  border-bottom: 1px solid rgba(166, 245, 93, 0.5);
  pointer-events: none;
}

.picker-scroll {
  height: 100%;
  overflow-y: auto;
  scroll-behavior: smooth;
  scroll-snap-type: y mandatory;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.picker-scroll::-webkit-scrollbar {
  display: none;
}

.picker-padding {
  height: 68px;
  flex-shrink: 0;
}

.picker-item {
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.4);
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  scroll-snap-align: center;
  user-select: none;
}

.picker-item.selected {
  color: rgba(255, 255, 255, 1);
  font-size: 18px;
  font-weight: 600;
}

.picker-item:hover {
  color: rgba(255, 255, 255, 0.8);
}

/* 移动端选择器优化 */
@media (max-width: 768px) {
  .body-type-picker {
    width: 100%;
  }
  
  .body-type-list {
    padding: 10px 50px;
  }
  
  .body-type-item {
    width: 80px;
  }
  
  .body-type-image {
    width: 60px;
    height: 100px;
  }
  
  .picker-container {
    width: 100%;
    height: 160px;
  }
}
</style>

